/**
 * One reading of GET /dispute-chart for every dashboard widget (Home + Disputes), so they
 * always show the same numbers.
 *
 * pi_chart_data holds TWO overlapping partitions of the same disputes:
 *   - outcome:  Won + Pending + Lost + RDR             (= every dispute once)
 *   - response: Responded + Not Responded (non-RDR)    (= the same disputes again)
 * Summing all six buckets double-counted (62 shown for 31). The donut is built from the
 * outcome partition only; responded / not responded are reported separately.
 *
 * `totals` (total / primary / secondary / rdr) comes from the backend; when an older API
 * omits it, total falls back to the outcome partition and the new/follow-up split is null.
 */
export const DISPUTE_WON = 'Currently resolved in Merchant\'s favor';
export const DISPUTE_LOST = 'Currently resolved in Bank\'s favor';
export const DISPUTE_PENDING = 'Pending';
export const DISPUTE_RDR = 'RDR';
export const DISPUTE_RESPONDED = 'Responded';
export const DISPUTE_NOT_RESPONDED = 'Not Responded';

export const DISPUTE_OUTCOME_TYPES = [DISPUTE_WON, DISPUTE_PENDING, DISPUTE_LOST, DISPUTE_RDR];

export interface DisputeChartSummary {
  /** every dispute in the range (= rows of the disputes list) */
  total: number;
  /** new chargebacks (is_primary_case); null when the API doesn't send totals */
  primary: number | null;
  /** follow-up records of a chargeback: reversals, pre-arbitration, credits; null as above */
  secondary: number | null;
  won: number;
  lost: number;
  pending: number;
  rdr: number;
  responded: number;
  notResponded: number;
  /** won of resolved (won + lost), %; null when nothing is resolved */
  winRate: number | null;
  /** the outcome partition only — what the donut draws */
  outcome: { data_type: string; dispute_count: number }[];
}

export function summarizeDisputeChart(res: any): DisputeChartSummary {
  const pi: any[] = res?.pi_chart_data ?? [];
  const count = (type: string) => Number(pi.find(r => r.data_type === type)?.dispute_count || 0);

  const won = count(DISPUTE_WON);
  const lost = count(DISPUTE_LOST);
  const outcome = DISPUTE_OUTCOME_TYPES.map(type => ({data_type: type, dispute_count: count(type)}));
  const outcomeTotal = outcome.reduce((sum, r) => sum + r.dispute_count, 0);
  const totals = res?.totals;

  return {
    total: totals ? Number(totals.total || 0) : outcomeTotal,
    primary: totals ? Number(totals.primary || 0) : null,
    secondary: totals ? Number(totals.secondary || 0) : null,
    won,
    lost,
    pending: count(DISPUTE_PENDING),
    rdr: count(DISPUTE_RDR),
    responded: count(DISPUTE_RESPONDED),
    notResponded: count(DISPUTE_NOT_RESPONDED),
    winRate: won + lost > 0 ? Math.round((won / (won + lost)) * 100) : null,
    outcome,
  };
}
